import useSupporter from "../hooks/useSupporter";
import { useScopedField } from "@ngt/forms-core";
import { FC, useEffect, useState } from "react";
import { Text } from "@ngt/forms";
import { Box, CircularProgress, InputAdornment } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faExclamation } from '@fortawesome/pro-solid-svg-icons';
import ApplicationField from "@/fields/ApplicationField";

interface ISupporterProps {
    propertyPath: string;
    label: string;
}

const useDebouncedValue = <T,>(value: T, debounceTime: number = 500) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const id = setTimeout(() => {
            setDebouncedValue(value);
        }, debounceTime);

        return () => {
            clearTimeout(id);
        }
    }, [debounceTime, value, setDebouncedValue]);

    return debouncedValue;
};

const Supporter: FC<ISupporterProps> = ({propertyPath, label}) => {
    const { state: { value: supporterName } } = useScopedField<string>(propertyPath, { value: true });
    const { context: { setValue: setSupporterId } } = useScopedField<string>(propertyPath + 'Id', {});

    const debouncedSupporterName = useDebouncedValue(supporterName);

    const { data: supporterId, loading: checkingSupporter, validating: updatingSupporter } = useSupporter(debouncedSupporterName);

    useEffect(() => {
        if (supporterName === debouncedSupporterName) {
            setSupporterId(supporterId);
        }
        else {
            setSupporterId(undefined);
        }
    }, [supporterName, debouncedSupporterName, supporterId, setSupporterId]);

    return (
        <ApplicationField
            label={label}
            propertyPath={propertyPath}
            component={Text}
            margin="normal"
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        {
                            (checkingSupporter || updatingSupporter) && (
                                <CircularProgress size={20} />
                            )
                        }
                        {
                            !checkingSupporter && !updatingSupporter && (
                                
                                <Box sx={{marginRight: '10px', color: supporterId ? 'success.main' : 'error.main' }}>
                                    <FontAwesomeIcon size='2x' icon={supporterId ? faCheck : faExclamation} />
                                </Box>
                            )
                        }
                    </InputAdornment>
                )
            }}
        />
    );
}

export default Supporter;